// La Biblioteca de prompts, el pop-up — ADR-2026-10-02-biblioteca-de-prompts.
//
// La misma ventana que el selector de "agregar" del Agente (`SelectorDeImagenModal`:
// centrada, 980 de ancho, esquinas de 14, entra con zoom mínimo y fundido), con la
// navegación en una columna a la IZQUIERDA bajo el título "Biblioteca de prompts":
// Historial y Favoritos arriba y, separados por una línea, los cuatro catálogos de
// fragmentos. A la derecha, el contenido:
//
//  • Historial / Favoritos: filas de texto. Tocar una REEMPLAZA el prompt. Cada fila lleva
//    una estrella para marcarla o sacarla de Favoritos.
//  • Catálogos: una grilla de cards —imagen, título y una línea de descripción, todo del
//    mismo tamaño de letra y la descripción apagada—. Tocar una SUMA su fragmento al
//    prompt (`conFragmento`) y el pop-up se queda abierto: se pueden encadenar.
//
// Es un componente propio y no `SelectorDeImagenModal` porque aquél es una grilla de
// miniaturas con pestañas arriba; lo que acá se parece es la ventana, no el adentro.

import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { Aperture, Check, Clock, ImageIcon, Plus, Smile, Sparkles, Star, Sun, Video, X, type LucideIcon } from 'lucide-react';
import { useExitTransition } from '../../../../hooks/useExitTransition';
import { useAgentEnv } from '../../agentEnvContext';
import { fetchPromptHistory, type PromptHistoryTipo } from '../../promptHistory';
import {
  CATEGORIAS_DE_BIBLIOTECA,
  FRAGMENTOS,
  alternarFavorito,
  conFragmento,
  guardarFavoritos,
  imagenDeFragmento,
  leerFavoritos,
  posterDeFragmento,
  videoDeFragmento,
  type CategoriaDeBiblioteca,
  type FragmentoDePrompt,
} from './fragmentosDePrompt';

type Seccion = 'historial' | 'favoritos' | CategoriaDeBiblioteca;

const DURACION_MS = 200;

const ICONOS: Record<Seccion, LucideIcon> = {
  historial: Clock,
  favoritos: Star,
  angulos: Aperture,
  iluminacion: Sun,
  tipos: ImageIcon,
  efectos: Sparkles,
  camara: Video,
  emociones: Smile,
};

/**
 * Una card de fragmento. Con `video` (Movimientos de cámara) es un loop mudo con su poster,
 * en 16:9 como vienen los clips; si no, la imagen de `public/` en 4:3, y sin imagen (el
 * archivo no está) el título va sobre un fondo liso. Debajo, el texto a la izquierda y a la
 * derecha la pastilla "Añadir prompt", que al tocarla pasa a "Prompt añadido" (texto que
 * entra con un fundido corto y el ícono de más que se vuelve tilde). `anadido` lo decide el
 * padre mirando si el fragmento YA está en el prompt, así la pastilla dice la verdad también
 * al abrir el pop-up o si el usuario lo borró a mano. Tocar la imagen añade igual.
 */
const CardDeFragmento: React.FC<{
  categoria: CategoriaDeBiblioteca;
  fragmento: FragmentoDePrompt;
  anadido: boolean;
  onElegir: () => void;
}> = ({ categoria, fragmento: { id, titulo, descripcion, video }, anadido, onElegir }) => {
  const [sinImagen, setSinImagen] = useState(false);
  const Icono = anadido ? Check : Plus;
  return (
    <div className="group flex min-w-0 flex-col">
      <button
        type="button"
        onClick={onElegir}
        aria-label={`${titulo}: añadir al prompt`}
        className={`relative block w-full overflow-hidden rounded-xl bg-[#EEF0F5] ring-1 ring-black/[0.05] ${video ? 'aspect-video' : 'aspect-[4/3]'}`}
      >
        {video ? (
          <video
            src={videoDeFragmento(categoria, id)}
            poster={posterDeFragmento(categoria, id)}
            autoPlay
            muted
            loop
            playsInline
            preload="metadata"
            className="absolute inset-0 h-full w-full object-cover"
          />
        ) : !sinImagen && (
          <img
            src={imagenDeFragmento(categoria, id)}
            alt=""
            loading="lazy"
            onError={() => setSinImagen(true)}
            className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.03]"
          />
        )}
        <span className="absolute inset-0 rounded-xl ring-inset ring-brand-primary transition-shadow group-hover:ring-2" aria-hidden="true" />
      </button>
      {/* Arriba, el título y la pastilla centrados en la misma línea; abajo, la descripción a todo
          el ancho. Título y descripción del MISMO tamaño; la descripción se distingue por el color. */}
      <div className="mt-3.5 flex items-center justify-between gap-3">
        <span className="min-w-0 flex-1 truncate text-[13px] font-semibold text-brand-black/90">{titulo}</span>
        <button
          type="button"
          onClick={onElegir}
          aria-pressed={anadido}
          className={`inline-flex shrink-0 items-center gap-1.5 rounded-full py-1.5 pl-2.5 pr-3 text-[12.5px] font-medium transition-colors ${
            anadido ? 'bg-[#16A34A14] text-[#15803D]' : 'bg-black/[0.05] text-brand-black/80 hover:bg-black/[0.08]'
          }`}
        >
          {/* `key` por estado: al cambiar se vuelven a montar y corre la animación de entrada.
              Keys DISTINTAS entre ícono y texto: con la misma, React dejaba el "+" viejo al
              lado del tilde. */}
          <Icono key={anadido ? 'icono-si' : 'icono-no'} className="h-3.5 w-3.5 animate-prompt-anadido" strokeWidth={2.5} aria-hidden="true" />
          <span key={anadido ? 'texto-si' : 'texto-no'} className="animate-prompt-anadido">{anadido ? 'Prompt añadido' : 'Añadir prompt'}</span>
        </button>
      </div>
      <span className="mt-2 block text-[13px] leading-snug text-[#02062B]/[0.45]">{descripcion}</span>
    </div>
  );
};

export const BibliotecaDePrompts: React.FC<{
  abierto: boolean;
  onCerrar: () => void;
  prompt: string;
  onPromptChange: (v: string) => void;
  /** De qué historial se lee: el de Prompt libre en imagen, el del Creador en video. */
  tipoDeHistorial: PromptHistoryTipo;
}> = ({ abierto, onCerrar, prompt, onPromptChange, tipoDeHistorial }) => {
  const { companyId } = useAgentEnv();
  const { mounted, animated } = useExitTransition(abierto, DURACION_MS);
  const [seccion, setSeccion] = useState<Seccion>('historial');
  const [historial, setHistorial] = useState<string[] | null>(null);
  const [favoritos, setFavoritos] = useState<string[]>(() => leerFavoritos(companyId));

  useEffect(() => {
    if (!abierto) return;
    setHistorial(null);
    let vivo = true;
    void fetchPromptHistory(companyId, tipoDeHistorial, 30).then((l) => {
      if (vivo) setHistorial(l);
    });
    setFavoritos(leerFavoritos(companyId));
    return () => {
      vivo = false;
    };
  }, [abierto, companyId, tipoDeHistorial]);

  useEffect(() => {
    if (!abierto) return;
    const alTeclear = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCerrar();
    };
    document.addEventListener('keydown', alTeclear);
    return () => document.removeEventListener('keydown', alTeclear);
  }, [abierto, onCerrar]);

  const alternar = (p: string) => {
    if (!companyId) return;
    const lista = alternarFavorito(favoritos, p);
    setFavoritos(lista);
    guardarFavoritos(companyId, lista);
  };

  // Las categorías "sólo video" (Movimientos de cámara) no se ofrecen cuando el medio es
  // imagen: un movimiento de cámara no le dice nada a una foto.
  const esVideo = tipoDeHistorial === 'video';
  const secciones = useMemo<{ id: Seccion; nombre: string; separado?: boolean }[]>(
    () => [
      { id: 'historial', nombre: 'Historial' },
      { id: 'favoritos', nombre: 'Favoritos' },
      ...CATEGORIAS_DE_BIBLIOTECA.filter((c) => esVideo || !c.soloVideo).map((c, i) => ({ id: c.id, nombre: c.nombre, separado: i === 0 })),
    ],
    [esVideo],
  );
  useEffect(() => {
    if (!secciones.some((s) => s.id === seccion)) setSeccion('historial');
  }, [secciones, seccion]);

  if (!mounted) return null;

  const filas = (lista: string[] | null, vacio: string) =>
    lista === null ? (
      <div className="flex flex-col gap-2" aria-hidden="true">
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className="h-[52px] animate-pulse rounded-xl bg-black/[0.05]" />
        ))}
      </div>
    ) : lista.length === 0 ? (
      <p className="py-10 text-center text-[13.5px] text-[#02062B]/[0.45]">{vacio}</p>
    ) : (
      <ul className="flex flex-col gap-1.5">
        {lista.map((p) => {
          const esFavorito = favoritos.includes(p);
          return (
            <li key={p} className="group flex items-start gap-2 rounded-xl transition-colors hover:bg-black/[0.035]">
              <button
                type="button"
                onClick={() => {
                  onPromptChange(p);
                  onCerrar();
                }}
                className="min-w-0 flex-1 px-3 py-2.5 text-left text-[13.5px] leading-snug text-brand-black/90"
              >
                <span className="line-clamp-3">{p}</span>
              </button>
              <button
                type="button"
                onClick={() => alternar(p)}
                aria-label={esFavorito ? 'Sacar de favoritos' : 'Guardar en favoritos'}
                aria-pressed={esFavorito}
                className={`mr-2 mt-2 inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full transition-colors ${
                  esFavorito ? 'text-brand-primary' : 'text-[#02062B]/[0.3] opacity-0 hover:bg-black/[0.05] group-hover:opacity-100'
                }`}
              >
                <Star className="h-4 w-4" strokeWidth={2} fill={esFavorito ? 'currentColor' : 'none'} />
              </button>
            </li>
          );
        })}
      </ul>
    );

  const contenido =
    seccion === 'historial' ? (
      filas(historial, 'Todavía no escribiste ningún prompt acá.')
    ) : seccion === 'favoritos' ? (
      filas(favoritos, 'Marcá con la estrella los prompts que quieras tener a mano.')
    ) : (
      // Los fragmentos con `grupo` (Movimientos de cámara) van en bloques con un subtítulo
      // cada uno, en el orden en que aparece el primer fragmento de cada grupo.
      <div className="flex flex-col gap-6">
        {Array.from(
          FRAGMENTOS[seccion].reduce((m, f) => m.set(f.grupo ?? '', [...(m.get(f.grupo ?? '') ?? []), f]), new Map<string, FragmentoDePrompt[]>()),
        ).map(([grupo, lista]) => (
          <section key={grupo}>
            {grupo && <h5 className="mb-3 text-[12px] font-semibold uppercase tracking-wide text-[#02062B]/[0.45]">{grupo}</h5>}
            {/* Dos columnas en todas: el movimiento de los clips se ve, y la pastilla "Añadir prompt"
                entra al lado del texto (a cuatro columnas no entraba). */}
            <div className="grid grid-cols-2 gap-x-4 gap-y-5">
              {lista.map((f) => (
                <CardDeFragmento
                  key={f.id}
                  categoria={seccion}
                  fragmento={f}
                  anadido={prompt.toLowerCase().includes(f.prompt.toLowerCase())}
                  onElegir={() => onPromptChange(conFragmento(prompt, f.prompt))}
                />
              ))}
            </div>
          </section>
        ))}
      </div>
    );

  return createPortal(
    <div
      className={`fixed inset-0 z-[130] flex items-center justify-center p-4 transition-opacity duration-200 ease-out ${animated ? 'opacity-100' : 'opacity-0'}`}
      role="dialog"
      aria-modal="true"
      aria-label="Biblioteca de prompts"
    >
      <button type="button" aria-label="Cerrar" onClick={onCerrar} className="absolute inset-0 h-full w-full cursor-default bg-black/45 backdrop-blur-[2px]" />
      <div
        className={`relative z-10 flex h-[min(84vh,672px)] w-full max-w-[980px] overflow-hidden rounded-[14px] border border-neutral-200 bg-surface-card shadow-xl transition-[transform,opacity] duration-200 ease-out ${
          animated ? 'scale-100 opacity-100' : 'scale-95 opacity-0'
        }`}
      >
        {/* Izquierda: la navegación. */}
        <nav className="flex w-[220px] shrink-0 flex-col border-r border-black/[0.06] bg-[#FAFAFC] px-3 pb-3 pt-3.5" aria-label="Secciones de la biblioteca">
          <h3 className="mb-3 px-3 pt-0.5 text-[15px] font-semibold text-brand-black/90">Biblioteca de prompts</h3>
          {secciones.map((s) => {
            const Icono = ICONOS[s.id];
            const activa = s.id === seccion;
            return (
              <React.Fragment key={s.id}>
                {s.separado && <span className="my-2 block h-px bg-black/[0.07]" aria-hidden="true" />}
                <button
                  type="button"
                  onClick={() => setSeccion(s.id)}
                  aria-current={activa ? 'page' : undefined}
                  className={`flex items-center gap-2.5 rounded-xl px-3 py-2 text-left text-[13.5px] font-medium transition-colors ${
                    activa ? 'bg-white text-brand-primary shadow-sm ring-1 ring-black/[0.05]' : 'text-brand-black/75 hover:bg-black/[0.04]'
                  }`}
                >
                  <Icono className="h-4 w-4 shrink-0" strokeWidth={2} />
                  {s.nombre}
                </button>
              </React.Fragment>
            );
          })}
        </nav>
        {/* Derecha: el contenido de la sección, con su cabecera. */}
        <div className="flex min-w-0 flex-1 flex-col">
          <div className="flex shrink-0 items-center justify-between gap-3 border-b border-black/[0.06] px-5 pb-3 pt-3.5">
            <div className="flex min-w-0 items-baseline gap-3">
              <h4 className="text-[15px] font-semibold text-brand-black/90">
                {secciones.find((s) => s.id === seccion)?.nombre}
              </h4>
              {seccion !== 'historial' && seccion !== 'favoritos' && (
                <span className="truncate text-[12.5px] text-[#02062B]/[0.45]">Tocá una para sumarla al prompt</span>
              )}
            </div>
            <button
              type="button"
              onClick={onCerrar}
              aria-label="Cerrar"
              className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-brand-black/40 transition-colors hover:bg-black/[0.06] hover:text-brand-black/70"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">{contenido}</div>
        </div>

      </div>
    </div>,
    document.body,
  );
};

export default BibliotecaDePrompts;
